import * as Dialog from "@radix-ui/react-dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { ReactComponent as CloseSVG } from "images/icons/x.svg"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import { useRef } from "react"
import styled, { css, keyframes } from "styled-components"
import colors from "styles/colors"

export default function VideoModal({
	isOpen,
	setIsOpen,
}: {
	isOpen: boolean
	setIsOpen: (isOpen: boolean) => void
}) {
	const contentInnerRef = useRef<HTMLDivElement>(null)

	return (
		<Dialog.Root
			open={isOpen}
			onOpenChange={(newOpen) => {
				setIsOpen(newOpen)
			}}
		>
			<Dialog.Portal>
				<Overlay />
				<Content
					ref={contentInnerRef}
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<VisuallyHidden asChild>
						<Dialog.Title>Response Demo Video</Dialog.Title>
					</VisuallyHidden>
					<VisuallyHidden asChild>
						<Dialog.Description>
							A demonstration of Response's products and services.
						</Dialog.Description>
					</VisuallyHidden>
					<IFrame
						title="Response Demo"
						src="https://fast.wistia.net/embed/iframe/xs4slrq90k"
						allow="autoplay; fullscreen"
						allowFullScreen
					/>
					<Dialog.Close asChild>
						<Close onClick={() => setIsOpen(!isOpen)} />
					</Dialog.Close>
				</Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const Overlay = styled(Dialog.Overlay)`
	position: fixed;
	inset: 0;
	z-index: 10;
	backdrop-filter: blur(5px);

	&[data-state="open"] {
		animation: ${fadeIn} 0.5s ease-out;
	}

	&[data-state="closed"] {
		animation: ${fadeOut} 0.5s ease-out;
	}
`

const Content = styled(Dialog.Content)`
	${fresponsive(css`
		position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    width: 100vw;
    height: 95vh;
    overflow: hidden;
    padding: 100px;
	`)}

	${ftablet(css`
		--height: min(95vh, 900px);

		/* stylelint-disable plugin/no-unsupported-browser-features */
		@supports (height: min(95dvh, 900px)) {
			--height: min(95dvh, 900px);
		}

		height: var(--height);
		padding: 100px;
	`)}

  ${fmobile(css`
		padding: 0;
		height: 75vh;
    width: 90vw;
	`)}

  &[data-state="open"] {
		animation: ${fadeIn} 0.5s ease-out;
	}

	&[data-state="closed"] {
		animation: ${fadeOut} 0.5s ease-out;
	}
`

const IFrame = styled.iframe`
	width: 100%;
	height: 100%;
	border: none;
`

const Close = styled(CloseSVG)`
	${fresponsive(css`
		position: absolute;
		top: 50px;
		right: 50px;
		width: 33px;
		height: 33px;
		z-index: 11;

		path {
			stroke: ${colors.charcoal};
		}

		&:hover {
			cursor: pointer;
		}
	`)}

	${fmobile(css`
		width: 22px;
		height: 22px;
		top: 20px;
		right: 20px;
	`)}
`
